import { ApolloError } from "@apollo/client";
import { Alert, Button, Card, Spinner, Text, TextInput } from "north.js";
import React, { FormEvent, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { useRequestTicketMutation } from "./graphql/requestTicket.generated";

type TRequestState =
  | "idle"
  | "submitting"
  | "accepted"
  | "withdrawn"
  | "rejected"
  | "error";

const Register: React.FC = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [requestError, setRequestError] = useState("");
  const [requestState, setRequestState] = useState<TRequestState>("idle");
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const [requestTicket] = useRequestTicketMutation({
    context: { unauthenticated: true },
  });

  useEffect(() => {
    if (
      ["accepted", "withdrawn", "rejected"].includes(requestState) ||
      requestError
    ) {
      resultRef.current?.focus();
    }
  }, [requestError, requestState]);

  useEffect(() => {
    if (cooldownEndsAt === null) return;

    const updateCooldown = () => {
      const seconds = Math.max(
        Math.ceil((cooldownEndsAt - Date.now()) / 1000),
        0
      );
      setCooldownSeconds(seconds);
      if (seconds === 0) setCooldownEndsAt(null);
    };
    const timer = window.setInterval(updateCooldown, 1000);
    document.addEventListener("visibilitychange", updateCooldown);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", updateCooldown);
    };
  }, [cooldownEndsAt]);

  const validateEmail = (value: string) => {
    if (!value.trim()) {
      return "Enter the email associated with your registration.";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
      return "Invalid email.";
    }
    return "";
  };

  const submitRequest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validationError = validateEmail(email);
    setEmailError(validationError);
    setRequestError("");
    if (validationError) return;

    setRequestState("submitting");

    try {
      const { data } = await requestTicket({
        variables: { email: email.trim() },
      });
      const status = data?.requestTicket.status;

      if (
        status !== "accepted" &&
        status !== "withdrawn" &&
        status !== "rejected"
      ) {
        setRequestState("error");
        setRequestError(
          "We couldn't process your request. Please try again shortly."
        );
        return;
      }

      setRequestState(status);
      setCooldownSeconds(status === "accepted" ? 60 : 0);
      setCooldownEndsAt(status === "accepted" ? Date.now() + 60000 : null);
    } catch (error) {
      const apolloError = error as ApolloError;
      const errorCode = apolloError.graphQLErrors[0]?.extensions?.code;
      const errorMessage = apolloError.graphQLErrors[0]?.message;
      const statusCode = (
        apolloError.networkError as { statusCode?: number } | null
      )?.statusCode;

      if (
        statusCode === 404 ||
        errorCode === "NOT_FOUND" ||
        errorMessage === "No eligible attendee found."
      ) {
        setRequestState("idle");
        setEmailError("That email is not recognized.");
      } else if (errorCode === "BAD_USER_INPUT") {
        setRequestState("idle");
        setEmailError("Invalid email.");
      } else {
        setRequestState("error");
        setRequestError(
          statusCode === 429 || errorCode === "TOO_MANY_REQUESTS"
            ? "A ticket was requested recently. Please wait a few minutes before trying again."
            : apolloError.networkError && !statusCode
            ? "We couldn't reach the ticket service. Check your connection and try again."
            : "We couldn't process your request. Please try again shortly."
        );
      }
    }
  };

  const result =
    requestState === "withdrawn"
      ? {
          color: "danger" as const,
          title: "Registration withdrawn",
          message:
            "Your application for Hack the North 2026 has been withdrawn and we're no longer able to offer you a spot for this year's event.",
        }
      : requestState === "rejected"
      ? {
          color: "danger" as const,
          title: "No ticket available",
          message:
            "Thank you for applying to Hack the North 2026. While we aren't able to offer you a spot this year, we're grateful you took the time to share your story with us.",
        }
      : null;

  return (
    <Page>
      <Main>
        <TicketShell elevation="lg">
          <FormPanel>
            <Title>
              <Text type="h1">Email my ticket</Text>
            </Title>
            <FormContent>
              {requestState === "accepted" ? (
                <AlertSpacing ref={resultRef} role="status" tabIndex={-1}>
                  <TicketAlert color="success" title="Email successfully sent.">
                    Check your inbox and spam folder. It may take a few minutes.
                  </TicketAlert>
                </AlertSpacing>
              ) : null}

              {result ? (
                <div ref={resultRef} role="status" tabIndex={-1}>
                  <TicketAlert color={result.color} title={result.title}>
                    {result.message}
                  </TicketAlert>
                </div>
              ) : null}

              {requestError ? (
                <AlertSpacing ref={resultRef} role="alert" tabIndex={-1}>
                  <TicketAlert
                    color="danger"
                    title="Unable to email your ticket"
                    dismissable
                    onDismiss={() => setRequestError("")}
                  >
                    {requestError}
                  </TicketAlert>
                </AlertSpacing>
              ) : null}

              {!result ? (
                <form noValidate onSubmit={submitRequest}>
                  <label htmlFor="email">
                    <Text type="button">Registration email</Text>
                  </label>
                  <InputSpacing>
                    <FullWidthTextInput
                      id="email"
                      name="email"
                      type="email"
                      size="md"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={email}
                      aria-invalid={Boolean(emailError)}
                      aria-describedby={
                        emailError ? "email-error" : "email-help"
                      }
                      disabled={requestState === "submitting"}
                      error={
                        emailError ? (
                          <span id="email-error">{emailError}</span>
                        ) : undefined
                      }
                      hint={
                        emailError ? undefined : (
                          <span id="email-help">
                            Use the email from your application.
                          </span>
                        )
                      }
                      onBlur={() => setEmailError(validateEmail(email))}
                      onChange={(event) => {
                        setEmail(event.target.value);
                        if (emailError) {
                          setEmailError(validateEmail(event.target.value));
                        }
                      }}
                    />
                  </InputSpacing>
                  <FullWidthButton
                    color="primary"
                    size="lg"
                    type="submit"
                    disabled={
                      requestState === "submitting" ||
                      (requestState === "accepted" && cooldownSeconds > 0)
                    }
                    leadingIcon={
                      requestState === "submitting" ? <Spinner /> : undefined
                    }
                  >
                    {requestState === "submitting"
                      ? "Emailing ticket..."
                      : requestState === "accepted" && cooldownSeconds > 0
                      ? `Email another ticket in ${cooldownSeconds}s`
                      : "Email my ticket"}
                  </FullWidthButton>
                </form>
              ) : null}
            </FormContent>
          </FormPanel>
        </TicketShell>
      </Main>
    </Page>
  );
};

export default Register;

const Page = styled.div`
  min-width: 320px;
  min-height: 100vh;
  background: ${({ theme }) => theme.globalConstants.color.backgroundBlue};
  color: ${({ theme }) => theme.globalConstants.color.textDark};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
`;

const Main = styled.main`
  display: grid;
  width: min(640px, calc(100% - 40px));
  min-height: 100vh;
  margin: 0 auto;
  padding: 32px 0;
  place-items: center;

  @media (max-width: 820px) {
    width: min(100% - 24px, 560px);
    padding: 24px 0;
  }

  @media (max-width: 430px) {
    width: calc(100% - 16px);
  }
`;

const TicketShell = styled(Card)`
  width: 100%;
  max-width: 560px;

  && {
    padding: 0;
  }
`;

const TicketAlert = styled(Alert)`
  > div > div > span {
    display: block;
  }
`;

const AlertSpacing = styled.div`
  margin-bottom: 20px;
`;

const FormPanel = styled.div`
  display: flex;
  flex-direction: column;
  padding: 56px;

  @media (max-width: 820px) {
    padding: 40px 30px 42px;
  }

  @media (max-width: 430px) {
    padding: 35px 23px 37px;
  }
`;

const Title = styled.h1`
  margin: 0 0 32px;
`;

const FormContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
`;

const InputSpacing = styled.div`
  margin-top: 8px;
  margin-bottom: 20px;
`;

const FullWidthTextInput = styled(TextInput)`
  width: 100%;
`;

const FullWidthButton = styled(Button)`
  width: 100%;
`;
